/** Shared, read-only EML extraction boundary. HTML and attachments are never returned. */
export const MAX_EML_PARSE_BYTES = 5_000_000;

export type EmlPlainText = {subject: string; text: string};

type PostalMimeTextEntry = {type?: unknown; value?: unknown};
type PostalMimeTextMapEntry = {plain?: unknown};
type PostalMimeWithTextMap = {textMap?: unknown};
type PostalMimeNode = {parentNode?: unknown; contentDisposition?: unknown};

function hasAttachmentAncestor(candidate: unknown): boolean {
  const visited = new Set<object>();
  let current: unknown = candidate;
  while (current !== undefined) {
    if (typeof current !== 'object' || current === null || visited.has(current)) throw new EmlTextError('parse_failed');
    visited.add(current);
    const node = current as PostalMimeNode;
    if (typeof node.contentDisposition !== 'object' || node.contentDisposition === null) throw new EmlTextError('parse_failed');
    const parsed = (node.contentDisposition as {parsed?: unknown}).parsed;
    if (typeof parsed !== 'object' || parsed === null) throw new EmlTextError('parse_failed');
    const value = (parsed as {value?: unknown}).value;
    if (typeof value !== 'string') throw new EmlTextError('parse_failed');
    if (value.toLowerCase() === 'attachment') return true;
    current = node.parentNode;
  }
  return false;
}

export class EmlTextError extends Error {
  constructor(public readonly code: 'invalid_size' | 'parse_failed') {
    super(code);
    this.name = 'EmlTextError';
  }
}

let postalMimeModulePromise: Promise<typeof import('postal-mime')> | null = null;

async function loadPostalMime(): Promise<typeof import('postal-mime')> {
  if (!postalMimeModulePromise) {
    postalMimeModulePromise = import('postal-mime').catch((error: unknown) => {
      postalMimeModulePromise = null;
      throw error;
    });
  }
  return postalMimeModulePromise;
}

/**
 * Parse only the subject and plain-text body from an already bounded private copy.
 * The parsed HTML and attachment collection are deliberately never read or returned.
 */
export async function extractEmlPlainText(buffer: ArrayBuffer): Promise<EmlPlainText> {
  if (!buffer.byteLength || buffer.byteLength > MAX_EML_PARSE_BYTES) throw new EmlTextError('invalid_size');
  try {
    const {default: PostalMime} = await loadPostalMime();
    // postal-mime's public message.text is a presentation value: it may contain
    // HTML converted to text or an expanded inline message/rfc822 attachment.
    // Use the pinned parser's provenance map and accept only original text/plain
    // nodes. Fail closed if that pinned internal shape ever changes.
    const parser = new PostalMime({forceRfc822Attachments: true, maxRfc822NestingDepth: 0});
    const message = await parser.parse(buffer);
    const textMap = (parser as PostalMimeWithTextMap).textMap;
    if (!(textMap instanceof Map)) throw new EmlTextError('parse_failed');
    const plainParts: string[] = [];
    for (const [node, candidate] of textMap.entries() as Iterable<[unknown, PostalMimeTextMapEntry]>) {
      // postal-mime does not propagate an attached multipart container's
      // disposition to its text descendants. Retain the parser node key so
      // every candidate can be rejected when any ancestor is an attachment.
      if (hasAttachmentAncestor(node)) continue;
      if (candidate.plain === undefined) continue;
      if (!Array.isArray(candidate.plain)) throw new EmlTextError('parse_failed');
      for (const entry of candidate.plain as PostalMimeTextEntry[]) {
        if (entry.type !== 'text' || typeof entry.value !== 'string') throw new EmlTextError('parse_failed');
        plainParts.push(entry.value);
      }
    }
    const plainText = {
      subject: typeof message.subject === 'string' ? message.subject : '',
      text: plainParts.join('\n'),
    };
    if (!plainText.subject && !plainText.text) throw new EmlTextError('parse_failed');
    return plainText;
  } catch (error) {
    if (error instanceof EmlTextError) throw error;
    throw new EmlTextError('parse_failed');
  }
}

export function emlSearchText(message: EmlPlainText): string {
  return [message.subject, message.text].join('\n');
}

export function emlPreviewText(message: EmlPlainText): string {
  const subject = message.subject.trim() || '(No subject)';
  const body = message.text || '(No plain-text body is available. Use Download to inspect the original.)';
  return `Subject: ${subject}\n\n${body}`;
}

/**
 * Docket #37/#164 item B9: deterministic source metadata, preserved alongside the original
 * bytes -- never inferred, summarized, or used to connect a mailbox. Every field here is a literal
 * RFC 5322/RFC 2045 header value or attachment property postal-mime already parses onto its
 * `Email` result; nothing is re-derived from HTML or synthesized the way `message.text` can be
 * (see extractEmlPlainText()'s own doc comment above) -- From/To/CC/BCC/Date/Message-ID and
 * attachment filenames are genuine header/part values, not presentation text, so this reads them
 * directly rather than through that function's textMap-provenance guard.
 */
export type EmlAddress = {name: string; address: string};
export type EmlAttachmentMetadata = {filename: string | null; sha256: string | null};
export type EmlMetadata = {
  /** message.subject exactly as postal-mime decodes it (e.g. from a MIME encoded-word) -- this is
   * a genuine decode of the literal header value, not a reformat/reinterpretation the way Date's
   * own message.date field is, so it is read the same way extractEmlPlainText() already does. */
  subject: string | null;
  from: EmlAddress[];
  to: EmlAddress[];
  cc: EmlAddress[];
  bcc: EmlAddress[];
  /** The raw Date header value exactly as received (e.g. "Wed, 09 Sep 2026 14:24:12 -0400") -- never reparsed/reformatted, so its timezone offset is preserved verbatim rather than interpreted. */
  date: string | null;
  /** The raw Message-ID header value exactly as received, including its angle brackets. */
  messageId: string | null;
  attachments: EmlAttachmentMetadata[];
};

type PostalMimeMailbox = {name?: unknown; address?: unknown; group?: unknown};

/** Flattens postal-mime's Address shape (a mailbox, or a named group of mailboxes) into a plain list of real mailboxes -- a pure group label with no address of its own carries nothing to preserve. */
function flattenAddresses(value: unknown): EmlAddress[] {
  if (!Array.isArray(value)) return [];
  const result: EmlAddress[] = [];
  for (const entry of value) {
    if (typeof entry !== 'object' || entry === null) continue;
    const mailbox = entry as PostalMimeMailbox;
    if (Array.isArray(mailbox.group)) {
      result.push(...flattenAddresses(mailbox.group));
      continue;
    }
    if (typeof mailbox.address === 'string') {
      result.push({name: typeof mailbox.name === 'string' ? mailbox.name : '', address: mailbox.address});
    }
  }
  return result;
}

type PostalMimeHeader = {key?: unknown; value?: unknown};

/** postal-mime's own `date`/`messageId` fields are reformatted (e.g. Date is normalized to a UTC ISO string, losing the original timezone offset). Read the matching raw header line instead so the literal, undecoded value is preserved verbatim. */
function findRawHeader(headers: unknown, key: string): string | null {
  if (!Array.isArray(headers)) return null;
  for (const entry of headers as PostalMimeHeader[]) {
    if (typeof entry !== 'object' || entry === null) continue;
    if (entry.key === key && typeof entry.value === 'string' && entry.value !== '') return entry.value;
  }
  return null;
}

async function attachmentSha256(content: unknown): Promise<string | null> {
  let bytes: ArrayBuffer | null = null;
  // A plain `instanceof ArrayBuffer` check is realm-sensitive (it fails for an ArrayBuffer
  // constructed against a different global's ArrayBuffer, e.g. under jsdom test realms), so an
  // internal-slot check is used instead. ArrayBuffer.isView() is itself realm-independent.
  if (ArrayBuffer.isView(content)) bytes = content.buffer.slice(content.byteOffset, content.byteOffset + content.byteLength) as ArrayBuffer;
  else if (typeof content === 'object' && content !== null && Object.prototype.toString.call(content) === '[object ArrayBuffer]') bytes = content as ArrayBuffer;
  if (bytes === null) return null;
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map(byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Parses only deterministic source metadata from an already bounded private copy, independent of
 * extractEmlPlainText()'s own already-reviewed body-extraction path (a second parse of the same
 * bounded buffer, so that function's exact behavior stays untouched). Original bytes are never
 * read into this function's return value and are never mutated by parsing -- storage keeps
 * exactly what was uploaded.
 */
export async function extractEmlMetadata(buffer: ArrayBuffer): Promise<EmlMetadata> {
  if (!buffer.byteLength || buffer.byteLength > MAX_EML_PARSE_BYTES) throw new EmlTextError('invalid_size');
  try {
    const {default: PostalMime} = await loadPostalMime();
    const message = await new PostalMime({forceRfc822Attachments: true, maxRfc822NestingDepth: 0}).parse(buffer);
    const attachments: EmlAttachmentMetadata[] = [];
    if (Array.isArray(message.attachments)) {
      for (const attachment of message.attachments as Array<{filename?: unknown; content?: unknown}>) {
        attachments.push({
          filename: typeof attachment.filename === 'string' ? attachment.filename : null,
          sha256: await attachmentSha256(attachment.content),
        });
      }
    }
    return {
      subject: typeof message.subject === 'string' ? message.subject : null,
      from: flattenAddresses(message.from === undefined ? undefined : [message.from]),
      to: flattenAddresses(message.to),
      cc: flattenAddresses(message.cc),
      bcc: flattenAddresses(message.bcc),
      date: findRawHeader(message.headers, 'date'),
      messageId: findRawHeader(message.headers, 'message-id'),
      attachments,
    };
  } catch (error) {
    if (error instanceof EmlTextError) throw error;
    throw new EmlTextError('parse_failed');
  }
}
